import React from 'react';
import { Box, CircularProgress, Typography, Paper, Alert, AlertTitle } from '@mui/material';
import { StoreRouter } from './StoreRouter';
import { useStoresQuery } from '../../hooks/queries';
import { SUPPORTED_BRANDS } from '@byte-store-control/config';

/**
 * Extract API error details from an axios error
 */
function getApiError(error: unknown): { code?: string; message?: string } | null {
  const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
  if (axiosError?.response?.data) {
    return {
      code: axiosError.response.data.error,
      message: axiosError.response.data.message,
    };
  }
  return null;
}

/**
 * Wrapper component that ensures stores are fetched before rendering StoreRouter.
 * Uses TanStack Query for data fetching with automatic caching and deduplication.
 * This handles both the list view (/stores) and detail view (/stores/:id/:function).
 */
export const StoreManagementWrapper: React.FC = () => {
  const { isLoading, error } = useStoresQuery();

  if (error) {
    const apiError = getApiError(error);

    if (apiError?.code === 'BRAND_NOT_SUPPORTED') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', p: 3 }}>
          <Paper sx={{ maxWidth: 480, p: 4, textAlign: 'center' }}>
            <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
              <AlertTitle>Brand Not Supported</AlertTitle>
              {apiError.message}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              This app currently supports the following brands: <strong>{SUPPORTED_BRANDS.join(', ')}</strong>.
              Please switch to a supported brand context or contact your administrator.
            </Typography>
          </Paper>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">
          Error: {error instanceof Error ? error.message : 'An error occurred'}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Stores are loaded (cached in TanStack Query), render the router
  // Child components can access stores via useStoresQuery() - it will return cached data
  return <StoreRouter />;
};

export default StoreManagementWrapper;
